// Pure helpers for Settings → Audit Logs: readable action sentences, result
// labels, target labels and the before/after diff. No React, so they are
// unit-tested on their own (auditLogModel.test.js).

// Backend outcomes (internal/adminaudit: OutcomeSuccess / OutcomeFailure /
// OutcomeDenied) and how the console shows them.
export const OUTCOMES = {
  success: { label: "Success", tone: "success" },
  failure: { label: "Failed", tone: "warn" },
  denied: { label: "Denied", tone: "danger" },
};

export const outcomeMeta = (outcome) => OUTCOMES[outcome] || { label: outcome || "Unknown", tone: "neutral" };

// Target types the backend records today (adminaudit snapshots + Note callers).
export const TARGET_TYPES = {
  policy: "Policy",
  dbRole: "Database role",
  tenant: "Tenant",
};

export const targetTypeLabel = (type) => TARGET_TYPES[type] || type || "";

const changesOf = (event) => {
  const c = event && event.changes;
  return c && typeof c === "object" ? c : null;
};

// The object's name: changes.after.name, or changes.before.name (deletes have
// no "after"). Without changes there is no name.
export function changedName(event) {
  const c = changesOf(event);
  if (!c) return "";
  const pick = (side) => (side && typeof side === "object" && typeof side.name === "string" ? side.name.trim() : "");
  return pick(c.after) || pick(c.before);
}

// "<hostId>/<role>" → { hostId, role }. A role may itself hold a slash, so
// only the first one splits.
export function splitDbRoleTarget(targetId) {
  const s = String(targetId || "");
  const i = s.indexOf("/");
  if (i < 0) return { hostId: "", role: s };
  return { hostId: s.slice(0, i), role: s.slice(i + 1) };
}

const roleOf = (event) => splitDbRoleTarget(event && event.targetId).role;

const policySentence = (verb) => (event) => {
  const name = changedName(event);
  return name ? `${verb} policy ${name}` : `${verb} a policy`;
};

// First match wins: an entry matches when the action or the route contains one
// of its needles (case-insensitive). Order matters where needles overlap.
export const ACTION_RULES = [
  { match: ["policy/json/updatePolicy"], show: policySentence("Updated") },
  { match: ["policy/json/approvePolicy"], show: policySentence("Approved") },
  { match: ["policy/json/deletePolicy", "policyService/deletePolicyJSON"], show: policySentence("Deleted") },
  { match: ["policy/json/revokePolicy"], show: policySentence("Revoked") },
  { match: ["policy/json/createPolicy"], show: () => "Created a policy" },
  { match: ["policy/changeStatus"], show: () => "Changed a policy's status" },
  { match: ["database.noPolicy.set"], show: () => "Changed what database logins without a policy get" },
  {
    match: ["database.userPassword.manage"],
    show: (event) => {
      const after = changesOf(event) && changesOf(event).after;
      const enabled = after && typeof after === "object" ? after.enabled : undefined;
      const state = enabled === true ? "on" : enabled === false ? "off" : "on or off";
      const role = roleOf(event);
      return `Turned password management ${state} for ${role || "a database role"}`;
    },
  },
  {
    match: ["database.userPassword.rotate"],
    show: (event) => {
      const role = roleOf(event);
      return role ? `Rotated ${role}'s password` : "Rotated a database role's password";
    },
  },
  { match: ["settings/passwordRotation"], show: () => "Changed the password rotation interval" },
  { match: ["proxy/credentials/revoke"], show: () => "Revoked a database install credential" },
  { match: ["database/proxy/credentials", "databaseService/proxy/credentials"], show: () => "Issued a database install credential" },
  { match: ["LogOutputConfiguration"], show: () => "Changed the log output configuration" },
];

// The readable sentence for an audit event; anything unmapped is the raw action.
export function describeAction(event) {
  if (!event) return "";
  const action = String(event.action || "");
  const route = String(event.route || "");
  const hay = `${action}\n${route}`.toLowerCase();
  const rule = ACTION_RULES.find((r) => r.match.some((m) => hay.includes(m.toLowerCase())));
  if (rule) return rule.show(event);
  return action || [event.method, route].filter(Boolean).join(" ");
}

// The Target column: the policy's name when known, the role for a database
// role, else the raw id.
export function describeTarget(event) {
  if (!event || (!event.targetType && !event.targetId)) return { primary: "", secondary: "" };
  const type = targetTypeLabel(event.targetType);
  if (event.targetType === "policy") {
    return { primary: changedName(event) || event.targetId || "", secondary: type };
  }
  if (event.targetType === "dbRole") {
    const { hostId, role } = splitDbRoleTarget(event.targetId);
    return { primary: role || event.targetId || "", secondary: hostId ? `${type} · host #${hostId}` : type };
  }
  if (event.targetType === "tenant") {
    return { primary: event.targetId ? `Tenant #${event.targetId}` : type, secondary: "" };
  }
  return { primary: event.targetId || "", secondary: type };
}

export const actorLabel = (event) =>
  (event && event.actorEmail) || (event && event.actorUserId ? `User #${event.actorUserId}` : "Unknown");

// ── before/after diff ───────────────────────────────────────────────────────

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

// A JSON string that holds an object or array is treated as that value, so a
// policy stored as text diffs field by field.
export function parseMaybeJson(v) {
  if (typeof v !== "string") return v;
  const t = v.trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return v;
  try {
    return JSON.parse(t);
  } catch (_) {
    return v;
  }
}

// Nested keys → dotted paths, arrays as [i]: {a: {b: 1}, c: [2]} →
// {"a.b": 1, "c[0]": 2}. Empty objects/arrays stay as leaves.
export function flatten(value, prefix = "", out = {}) {
  const v = parseMaybeJson(value);
  if (Array.isArray(v)) {
    if (v.length === 0 && prefix) out[prefix] = [];
    v.forEach((item, i) => flatten(item, `${prefix}[${i}]`, out));
    return out;
  }
  if (isPlainObject(v)) {
    const keys = Object.keys(v);
    if (keys.length === 0 && prefix) out[prefix] = {};
    keys.forEach((k) => flatten(v[k], prefix ? `${prefix}.${k}` : k, out));
    return out;
  }
  if (prefix) out[prefix] = v;
  return out;
}

const sameLeaf = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const hasValue = (side) => side !== undefined && side !== null;

// Rows for the side-by-side table: [{ key, before, after, changed, inBefore,
// inAfter }]. Keys under a `collapse` prefix (e.g. "policy", shown in full
// underneath) are listed only when they changed. Order: top-level fields as the
// "after" side has them, then any only the "before" side has.
export function diffChanges(before, after, { collapse = [] } = {}) {
  const fb = hasValue(before) ? flatten(before) : {};
  const fa = hasValue(after) ? flatten(after) : {};
  const keys = [...Object.keys(fa)];
  Object.keys(fb).forEach((k) => {
    if (!(k in fa)) keys.push(k);
  });
  const collapsed = (k) => collapse.some((p) => k === p || k.startsWith(`${p}.`) || k.startsWith(`${p}[`));
  const rows = [];
  keys.forEach((key) => {
    const inBefore = key in fb;
    const inAfter = key in fa;
    const changed = hasValue(before) && hasValue(after) ? !(inBefore && inAfter && sameLeaf(fb[key], fa[key])) : false;
    if (collapsed(key) && !changed) return;
    rows.push({ key, before: fb[key], after: fa[key], changed, inBefore, inAfter });
  });
  return rows;
}

export function formatLeaf(v) {
  if (v === undefined) return "";
  if (v === null) return "null";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

// The full nested value under `key` on either side, for the collapsed JSON.
export function pretty(v) {
  if (!hasValue(v)) return "";
  return JSON.stringify(parseMaybeJson(v), null, 2);
}
